<template>
  <div class="agenda-cards" :class="{ 'agenda-cards--with-gap': attendee.enable_session_filtering }">
    <div class="agenda-items-card">
      <!-- Notify sections remain the same -->
      <div class="agenda-notify" v-if="notification?.enabled" :class="notification?.enabled ? 'mtb-desktop-15' : ''">
        <Notify :theme="notification?.type" v-html="notification?.text"></Notify>
      </div>

      <div class="agenda-notify" v-if="!isSingleSession && conferenceHasEnded" :class="notification?.enabled ? 'mtb-desktop-15' : ''">
        <Notify theme="primary">
          Now that your event has concluded make sure to visit the
          <a href="#" @click.prevent="goToCertificatePage()" style="text-decoration: underline;">certificate page</a>
          to complete your evaluations and access your certificate.
        </Notify>
      </div>

      <welcome-message />

      <additional-fixed-tabs
        v-show="attendee.enable_session_filtering || attendee?.tabs?.my_agenda"
        additionalFixedTabsClass="additional-fixed-tabs-class"
        :tabs="isMobile ? tabs : []"
      />

      <div v-if="!isFetching && attendee.enable_session_filtering" class="agenda-filter-mobile-days" :style="{ top: mobileFilterTop + 'px' }">
        <agenda-days-filter :filters="filters" @filter="handleChange" />
      </div>

      <div class="agenda-page" :style="{ '--agenda-sticky-offset': computedStickyTop + 'px' }">
        <!-- INDICATOR -->
        <div v-if="isFetching" class="page-loader">
          <c-loader />
        </div>

        <!-- EMPTY -->
        <div v-else-if="!isFetching && (!visibleSessions?.length || (filtersUsed && total === 0)) && !errorStatus">
          <c-list-empty
            style="margin-top: 50px;"
            title="The list is empty"
            :description="`${filtersUsed && total === 0 ? 'Clear the filter to display sessions in the list' : 'The list of sessions is empty.'}`"
          >
            <div style="margin: 0 auto;">
              <c-button v-if="filtersUsed && total === 0" theme="default" uppercase size="sm" fill icon="fa-filter-slash" @click="reset">
                Reset Filter
              </c-button>
            </div>
          </c-list-empty>
        </div>

        <!-- ERROR -->
        <div v-else-if="!isFetching && (!visibleSessions?.length || (filtersUsed && total === 0)) && errorStatus">
          <ErrorPage :status="errorStatus"/>
        </div>

        <!-- LIST VIEW -->
        <div v-else-if="!isGroupedView" class="agenda-group" ref="agendaGroup">
          <!-- Global sticky day header -->
          <div v-show="currentStickyDay" ref="stickyDayBar" class="sticky-day-header" :style="{ transform: `translateY(-${stickyPushOffset}px)` }">
            <div class="sticky-day-header__left">
              <!-- BACK BUTTON - показується тільки коли є navigationHistory -->
              <c-button
                v-if="navigationHistory && visibleGroupedSessions?.length === 1 && attendee.enable_time_blocks"
                theme="default"
                size="sm"
                icon="fa-arrow-left"
                @click="goBackToGroupedView"
                class="back-button"
              />
              <h3>{{ currentStickyDay }}</h3>
            </div>

            <!-- VIEW TOGGLE BUTTON -->
            <view-toggle
              v-if="!isFetching && flatSessions.length > 0 && attendee.enable_time_blocks"
              :tabs="viewTabs"
              :current-view="currentView"
              :hide-label-on-mobile="true"
              @view-changed="toggleView"
            />
          </div>

          <div v-if="visibleSessions.length" class="agenda-day">
            <div>
              <div
                v-for="(session, index) in visibleSessions"
                :key="session.id"
                class="agenda-list"
                :class="{ 'first-item': index === 0 }"
              >
                <!-- Item sticky day header -->
                <div class="agenda-list-day" v-if="shouldShowDay(index)">
                  <h3>{{ formatDate(session.happens_at) }}</h3>
                </div>
                <agenda-item
                  current-page="Full Schedule"
                  :session="session"
                  :index="index"
                  :allow-add-to-scheduler="attendee?.tabs?.my_agenda"
                  :group-time-frame="visibleSessions[index - 1] && visibleSessions[index - 1].start_at_formatted === visibleSessions[index].start_at_formatted && visibleSessions[index - 1].end_at_formatted === visibleSessions[index].end_at_formatted"
                  @setNavigationHistoryForSessionItem="setNavigationHistoryForSessionItem"
                />
              </div>

              <div v-if="visibleSessionsCount < flatSessions.length" class="load-more-trigger" v-observe-visibility="handleVisibilityChange">
                <c-button fill uppercase theme="default" icon="fa-solid fa-arrow-down" size="sm" :show-indicator="true" @click="loadMore">
                  Load More ...
                </c-button>
              </div>
            </div>
          </div>
        </div>

        <!-- GROUPED VIEW -->
        <div v-else class="agenda-group agenda-group--grouped" ref="agendaGroupGrouped">
          <!-- Global sticky day header for grouped view -->
          <div v-show="currentStickyDay" ref="stickyDayBarGrouped" class="sticky-day-header">
            <div class="sticky-day-header__left">
              <h3>{{ currentStickyDay }}</h3>
            </div>

            <!-- VIEW TOGGLE BUTTON -->
            <view-toggle
              v-if="!isFetching && flatSessions.length > 0 && attendee.enable_time_blocks"
              :tabs="viewTabs"
              :current-view="currentView"
              :hide-label-on-mobile="true"
              @view-changed="toggleView"
            />
          </div>

          <div v-if="visibleGroupedSessions.length" class="agenda-day">
            <div>
              <div
                v-for="(group, groupIndex) in visibleGroupedSessions"
                :key="`group-${group.timeSlot.start_at}-${group.timeSlot.end_at}`"
                class="agenda-list"
                :class="{ 'first-item': groupIndex === 0 }"
              >
                <!-- Day header for grouped view -->
                <div class="agenda-list-day" v-if="shouldShowDayGrouped(groupIndex)">
                  <h3>{{ formatDate(group.timeSlot.happens_at) }}</h3>
                </div>

                <!-- Grouped Item Component -->
                <agenda-grouped-item
                  :time-slot="group.timeSlot"
                  :sessions="group.sessions"
                  :topics="group.topics"
                  @filter-by-time="handleFilterByTime"
                />
              </div>

              <div v-if="visibleSessionsCount < groupedSessionsCount" class="load-more-trigger" v-observe-visibility="handleVisibilityChange">
                <c-button fill uppercase theme="default" icon="fa-solid fa-arrow-down" size="sm" :show-indicator="true" @click="loadMore">
                  Load More ...
                </c-button>
              </div>
            </div>
          </div>
        </div>

        <!-- Mobile Filter Modal -->
        <c-teleport to="body" v-if="isFilterOpen">
          <div class="mobile-filter-modal">
            <div class="mobile-filter-modal__content">
              <div class="mobile-filter-modal__header">
                <agenda-filter-top :total="total" @reset="reset" />
              </div>

              <div class="mobile-filter-modal__body">
                <agenda-filters :filters="filters" @filter="handleChange" />
              </div>

              <div class="mobile-filter-modal__footer">
                <c-button fill uppercase theme="default" icon="fa-xmark" @click="closeFilterMobile">Close</c-button>
                <c-button uppercase fill theme="primary" icon="fa-filter" @click="closeFilterMobile">Apply</c-button>
              </div>
            </div>
          </div>
        </c-teleport>
      </div>
    </div>

    <!-- Filter sidebar -->
    <div v-if="!isFetching && attendee.enable_session_filtering" class="agenda-filter-wrapper">
      <div class="agenda-filter">
        <div class="agenda-filter__top">
          <agenda-filter-top :total="total" @reset="reset" />
        </div>

        <agenda-filters :filters="filters" @filter="handleChange" />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { debounce } from "../../../../global-declare-components/helpers/debounce";
import AgendaItem from "../common/agenda-item";
import AgendaGroupedItem from "../common/agenda-grouped-item";
import AgendaFilters from "../../../components/agenda/agenda-filters";
import AdditionalFixedTabs from "../../../components/ui/AdditionalFixedTabs";
import AgendaDaysFilter from "../../../components/agenda/agenda-days-filter";
import AgendaFilterTop from "../../../components/agenda/agenda-filter-top";
import common from "../../../helpers/common";
import WelcomeMessage from "../common/welcome-message";
import Notify from "../../../components/ui/Notify";
import ErrorPage from "../../error-page";
import ViewToggle from "../../../components/ui/ViewToggle/index.vue";

export default {
  components: {
    ViewToggle,
    WelcomeMessage,
    AgendaDaysFilter,
    AgendaFilters,
    AgendaItem,
    AgendaGroupedItem,
    AdditionalFixedTabs,
    AgendaFilterTop,
    Notify,
    ErrorPage,
  },
  data() {
    return {
      attendeeId: this.$route.params.attendeeId,
      windowWidth: window.innerWidth,

      // Sticky header state
      currentStickyDay: '',
      stickyPushOffset: 0,
      computedStickyTop: 80,
      mobileFilterTop: 56,

      // IntersectionObserver handles
      _dayIO: null,
      _observedDayHeaders: [],

      // rAF throttle id for scroll fallback
      _scrollRaf: 0,

      viewTabs: [
        { value: 'list', label: 'List', icon: 'fa fa-list' },
        { value: 'grouped', label: 'Agenda', icon: 'fa fa-layer-group' }
      ]
    };
  },
  computed: {
    ...mapGetters('attendeeStore', [
      'attendee',
      'tabs',
      'notification',
      'isSingleSession',
      'conferenceHasEnded'
    ]),

    ...mapGetters('agendaStore', [
      'isFetching',
      'filters',
      'filtersUsed',
      'cachedFilters',
      'flatSessions',
      'total',
      'visibleSessionsCount',
      'defaultSessionsCount',
      'errorStatus',
      'isGroupedView',
      'groupedSessions',
      'groupedSessionsCount',
      'isFilterOpen',
      'navigationHistory',
    ]),

    visibleSessions() {
      return this.flatSessions.slice(0, this.visibleSessionsCount);
    },

    visibleGroupedSessions() {
      return this.groupedSessions.slice(0, this.visibleSessionsCount);
    },

    isMobile() {
      return this.windowWidth < 900;
    },

    currentView() {
      return this.isGroupedView ? 'grouped' : 'list';
    },
  },

  beforeRouteLeave(to, from, next) {
    if (to.name === 'Session Details' || to.name === 'session-details') {
      if (this.navigationHistory) {
        this.$store.commit('agendaStore/saveStateBeforeDetails', {
          navigationHistory: this.navigationHistory,
          scrollPosition: window.scrollY,
          currentView: this.isGroupedView ? 'grouped' : 'list',
        });
      }
    }
    next();
  },

  async mounted() {
    common.setHeaderText('Schedule');

    if (this.attendee?.conference?.is_single_session) {
      this.$router.replace({name: 'Single Event'});
    }

    await this.$store.dispatch("agendaStore/fetchAgenda", {
      attendeeId: this.attendeeId,
      conferenceId: this.attendee?.conference_id
    });

    const savedState = this.$store.getters['agendaStore/savedStateBeforeDetails'];

    if (savedState) {
      if (savedState.navigationHistory) {
        this.$store.commit('agendaStore/setNavigationHistory', savedState.navigationHistory);
      }

      const isGrouped = savedState.currentView === 'grouped';
      this.$store.commit("agendaStore/setIsGroupedView", isGrouped);

      this.$nextTick(() => {
        if (savedState.scrollPosition) {
          window.scrollTo({ top: savedState.scrollPosition, behavior: 'auto' });
        }
        this.rebuildDayObserver();
        this._requestScrollMeasure();
      });

      this.$store.commit('agendaStore/clearSavedStateBeforeDetails');
    } else {
      this.$store.commit("agendaStore/setIsGroupedView", false);
    }

    this._onResize = () => {
      this.windowWidth = window.innerWidth;
      this.computeStickyTop();
      this.rebuildDayObserver();
      this._requestScrollMeasure();
    };
    window.addEventListener('resize', this._onResize, { passive: true });

    this._onScroll = () => {
      this._requestScrollMeasure();
    };
    window.addEventListener('scroll', this._onScroll, { passive: true });

    this.$nextTick(() => {
      this.$nextTick(() => {
        this.computeStickyTop();
        this.rebuildDayObserver();
        this._requestScrollMeasure();
      });
    });
  },

  beforeDestroy() {
    this.disconnectDayObserver();
    window.removeEventListener('resize', this._onResize);
    window.removeEventListener('scroll', this._onScroll);
    if (this._scrollRaf) {
      cancelAnimationFrame(this._scrollRaf);
      this._scrollRaf = 0;
    }
  },

  watch: {
    visibleSessions() {
      this.$nextTick(() => {
        this.computeStickyTop();
        this.rebuildDayObserver();
        this._requestScrollMeasure();
      });
    },

    visibleGroupedSessions() {
      this.$nextTick(() => {
        this.computeStickyTop();
        this.rebuildDayObserver();
        this._requestScrollMeasure();
      });
    },

    isFetching(newVal) {
      if (!newVal) {
        this.$nextTick(() => {
          this.computeStickyTop();
          this.rebuildDayObserver();
          this._requestScrollMeasure();
        });
      }
    },

    isGroupedView() {
      this.$nextTick(() => {
        this.computeStickyTop();
        this.rebuildDayObserver();
        this._requestScrollMeasure();
      });
    }
  },

  methods: {
    setNavigationHistoryForSessionItem() {
      this.$store.commit('agendaStore/setNavigationHistory', {
        scrollPosition: window.scrollY,
        visibleSessionsCount: this.visibleSessionsCount,
        wasGroupedView: false
      });
    },

    handleFilterByTime({ start, end, dayId }) {
      this.$store.commit('agendaStore/setNavigationHistory', {
        scrollPosition: window.scrollY,
        visibleSessionsCount: this.visibleSessionsCount,
        wasGroupedView: true
      });

      const updatedFilters = JSON.parse(JSON.stringify(this.cachedFilters));

      updatedFilters.exact_time = {
        start_at_formatted: start,
        end_at_formatted: end
      };
      updatedFilters.timeframe = [];

      if (updatedFilters.days && Array.isArray(updatedFilters.days)) {
        updatedFilters.days = updatedFilters.days.map(day => ({
          ...day,
          is_checked: day.value === dayId
        }));
      }


      this.handleChange(updatedFilters);
      this.$store.dispatch('agendaStore/toggleGroupedView', false);

      this.$nextTick(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        this.rebuildDayObserver();
        this._requestScrollMeasure();
      });
    },

    goBackToGroupedView() {
      const history = this.navigationHistory;

      this.$store.dispatch("agendaStore/resetAgendaAndFilters").then(() => {
        this.$store.dispatch('agendaStore/toggleGroupedView', true);

        this.$nextTick(() => {
          if (history?.visibleSessionsCount) {
            this.$store.commit("agendaStore/updateVisibleSessionsCount", history.visibleSessionsCount);
          }

          this.$nextTick(() => {
            if (history?.scrollPosition) {
              window.scrollTo({ top: history.scrollPosition, behavior: 'smooth' });
            }

            this.rebuildDayObserver();
            this._requestScrollMeasure();
            this.$store.commit('agendaStore/clearNavigationHistory');
          });
        });
      });
    },

    goToCertificatePage() {
      this.$router.push({name: "My Certificate"});
    },

    toggleView(viewValue) {
      const isGrouped = viewValue === 'grouped';
      this.$store.commit('agendaStore/clearNavigationHistory');

      this.$store.dispatch("agendaStore/resetAgendaAndFilters").then(() => {
        this.$store.dispatch('agendaStore/toggleGroupedView', isGrouped);

        this.$nextTick(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
          this.rebuildDayObserver();
          this._requestScrollMeasure();
        });
      });
    },

    _cssVarPx(name, fallback = 0) {
      const raw = getComputedStyle(document.documentElement).getPropertyValue(name);
      const n = parseInt(raw, 10);
      return Number.isFinite(n) ? n : fallback;
    },

    computeStickyTop() {
      if (!this.isMobile) {
        this.computedStickyTop =
          this._cssVarPx('--attendee-header-height', 80) +
          this._cssVarPx('--attendee-gap-md', 0) +
          this._cssVarPx('--attendee-gap-lg', 0);
        return;
      }

      let top = this._cssVarPx('--attendee-header-mobile-height', 56);
      this.mobileFilterTop = top;

      const tabsEl = document.querySelector('.additional-fixed-tabs-class');
      if (tabsEl && tabsEl.offsetParent !== null) {
        const tabsHeight = Math.round(tabsEl.getBoundingClientRect().height);
        top += tabsHeight;
        this.mobileFilterTop += tabsHeight;
      }

      const daysEl = document.querySelector('.agenda-filter-mobile-days');
      if (daysEl && daysEl.offsetParent !== null) {
        top += Math.round(daysEl.getBoundingClientRect().height);
      }

      this.computedStickyTop = top;
    },

    getStickyTopOffset() {
      return this.computedStickyTop;
    },

    initDayObserver() {
      const topOffset = this.getStickyTopOffset();
      this._dayIO = new IntersectionObserver(
        this.onDayIntersect,
        {
          root: null,
          rootMargin: `-${topOffset}px 0px -85% 0px`,
          threshold: 0,
        }
      );
    },

    observeDayHeaders() {
      const container = this.isGroupedView
        ? this.$refs.agendaGroupGrouped
        : this.$refs.agendaGroup;
      if (!container) return;

      const headers = container.querySelectorAll('.agenda-list-day');
      this._observedDayHeaders = Array.from(headers);

      if (!this._observedDayHeaders || !this._observedDayHeaders.length) {
        return;
      }

      this._observedDayHeaders.forEach((el) => {
        if (!el.dataset.dayLabel) {
          const h3 = el.querySelector('h3');
          el.dataset.dayLabel = h3 ? h3.textContent.trim() : '';
        }
        this._dayIO.observe(el);
      });
    },

    disconnectDayObserver() {
      if (this._dayIO) {
        if (this._observedDayHeaders && this._observedDayHeaders.length) {
          this._observedDayHeaders.forEach((el) => this._dayIO.unobserve(el));
        }
        this._dayIO.disconnect();
        this._dayIO = null;
      }
      this._observedDayHeaders = [];
    },

    onDayIntersect(entries) {
      let latestLabel = null;

      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          latestLabel = entry.target.dataset.dayLabel || '';
        }
      });

      if (latestLabel) {
        this.currentStickyDay = latestLabel;
        return;
      }

      const topOffset = this.getStickyTopOffset();
      const headers = this._observedDayHeaders;
      if (!headers.length) {
        this.currentStickyDay = '';
        return;
      }

      let candidate = '';
      for (const el of headers) {
        const rect = el.getBoundingClientRect();
        if (rect.top - topOffset <= 0) {
          candidate = el.dataset.dayLabel || '';
        } else {
          break;
        }
      }
      if (!candidate) {
        candidate = headers[0].dataset.dayLabel || '';
      }
      this.currentStickyDay = candidate;
    },

    syncStickyWithFirstDay() {
      if (this.isGroupedView) {
        if (this.visibleGroupedSessions.length) {
          this.currentStickyDay = this.formatDate(this.visibleGroupedSessions[0].timeSlot.happens_at);
        } else {
          this.currentStickyDay = '';
        }
      } else {
        if (this.visibleSessions.length) {
          this.currentStickyDay = this.formatDate(this.visibleSessions[0].happens_at);
        } else {
          this.currentStickyDay = '';
        }
      }
      this.stickyPushOffset = 0;
    },

    rebuildDayObserver() {
      this.disconnectDayObserver();

      this.$nextTick(() => {
        this.initDayObserver();
        this.observeDayHeaders();
        this.syncStickyWithFirstDay();
      });
    },

    handleVisibilityChange(isVisible) {
      if (isVisible) {
        this.loadMore();
      }
    },

    formatDate(dateStr) {
      if (!dateStr) return '';

      const [year, month, day] = dateStr.split('-').map(Number);
      const date = new Date(year, month - 1, day);

      if (isNaN(date.getTime())) {
        console.warn('Invalid date string:', dateStr);
        return dateStr;
      }

      const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
      return date.toLocaleDateString('en-US', options);
    },

    shouldShowDay(index) {
      if (index === 0) return true;

      const current = this.visibleSessions[index];
      const previous = this.visibleSessions[index - 1];

      return current.start_at.slice(0, 10) !== previous.start_at.slice(0, 10);
    },

    shouldShowDayGrouped(groupIndex) {
      if (groupIndex === 0) return true;

      const current = this.visibleGroupedSessions[groupIndex];
      const previous = this.visibleGroupedSessions[groupIndex - 1];

      return current.timeSlot.happens_at !== previous.timeSlot.happens_at;
    },

    openFilterMobile() {
      this.$store.dispatch("agendaStore/openFilterMobile");
    },

    closeFilterMobile() {
      if (this.isGroupedView) {
        this.$store.commit("agendaStore/setIsGroupedView", false);

        this.$nextTick(() => {
          this.rebuildDayObserver();
          this._requestScrollMeasure();
        });
      }

      this.$store.dispatch("agendaStore/closeFilterMobile");
    },

    handleChange: debounce(function (filters) {
      this.$store.dispatch("agendaStore/updateAgendaAndFilters", filters).then(() => {
        this.$nextTick(() => {
          this.rebuildDayObserver();
          this._requestScrollMeasure();
        });
      });
    }, 500),

    reset() {
      this.$store.commit('agendaStore/clearNavigationHistory');

      this.$store.dispatch("agendaStore/resetAgendaAndFilters").then(() => {
        this.$nextTick(() => {
          this.rebuildDayObserver();
          this._requestScrollMeasure();
        });
      });
    },

    loadMore() {
      const maxCount = this.isGroupedView
        ? this.groupedSessionsCount
        : this.flatSessions.length;

      const visibleSessionsCount = Math.min(this.visibleSessionsCount + this.defaultSessionsCount, maxCount);
      this.$store.commit("agendaStore/updateVisibleSessionsCount", visibleSessionsCount);

      this.$nextTick(() => this._requestScrollMeasure());
    },

    _requestScrollMeasure() {
      if (this._scrollRaf) return;
      this._scrollRaf = requestAnimationFrame(() => {
        this._scrollRaf = 0;
        this.updateStickyDayBar();
      });
    },

    updateStickyDayBar() {
      const groupEl = this.isGroupedView
        ? this.$refs.agendaGroupGrouped
        : this.$refs.agendaGroup;
      if (!groupEl) return;

      const headers = Array.from(groupEl.querySelectorAll('.agenda-list-day'));
      if (!headers.length) {
        this.currentStickyDay = '';
        this.stickyPushOffset = 0;
        return;
      }

      const topOffset = this.getStickyTopOffset();
      const stickyBarHeight = 60;

      let activeIdx = 0;
      for (let i = 0; i < headers.length; i++) {
        const rect = headers[i].getBoundingClientRect();
        if (rect.top <= topOffset + stickyBarHeight) {
          activeIdx = i;
        } else {
          break;
        }
      }

      const activeHeader = headers[activeIdx];
      const titleEl = activeHeader?.querySelector('h3');
      const title = titleEl ? titleEl.textContent.trim() : '';

      if (this.currentStickyDay !== title) {
        this.currentStickyDay = title;
      }

      const stickyBar = this.isGroupedView
        ? this.$refs.stickyDayBarGrouped
        : this.$refs.stickyDayBar;
      const barRect = stickyBar ? stickyBar.getBoundingClientRect() : { height: 0 };
      const nextHeader = headers[activeIdx + 1];

      if (nextHeader) {
        const nextRect = nextHeader.getBoundingClientRect();
        const delta = nextRect.top - topOffset - barRect.height;
        this.stickyPushOffset = delta < 0 ? Math.min(barRect.height, Math.abs(delta)) : 0;
      } else {
        this.stickyPushOffset = 0;
      }
    },
  }
};
</script>

<style scoped lang="scss">
.agenda-list.first-item .agenda-list-day {
  visibility: hidden;
  height: 0 !important;
  min-height: 0 !important;
  margin: 0 !important;
  padding: 0 !important;
  border: none !important;
  overflow: hidden;
  line-height: 0;
}

.sticky-day-header {
  position: sticky;
  top: var(--agenda-sticky-offset);
  z-index: 21;
  background: white;
  padding: var(--attendee-gap-md) 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: transform 0.1s ease-out;

  @media (max-width: 900px) {
    padding: 5px var(--attendee-gap-md) !important;
  }

  h3 {
    margin: 0;
    color: var(--attendee-text-primary-color);
    font-size: 21px;
    margin-bottom: 0 !important;

    @media (max-width: 900px) {
      font-size: 16px !important;
      text-align: left !important;
      padding: 0 !important;
    }
  }

  @media (max-width: 900px) {
    padding: 0 var(--attendee-gap-md);
  }
}

.agenda-list-day {
  position: sticky;
  top: var(--agenda-sticky-offset);
  background-color: white;
  padding: var(--attendee-gap-md) 0;
  z-index: 18;

  h3 {
    margin-bottom: 0;
    color: var(--attendee-text-primary-color);
    font-size: 21px;

    @media (max-width: 900px) {
      font-size: 16px;
    }
  }

  @media (max-width: 900px) {
    padding: 0 var(--attendee-gap-md);
  }
}

.agenda-cards{
  display: grid;
  grid-template-columns: 1fr auto;
  position: relative;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
}

.agenda-cards--with-gap {
  grid-gap: var(--attendee-gap-md);
}

.agenda-items-card{
  padding: var(--attendee-gap-md);
  background-color: white;
  border-radius: var(--attendee-card-border-radius);
  box-shadow: var(--attendee-box-shadow);
  width: 100%;
  min-width: 1px;

  @media (max-width: 900px) {
    padding: 0;
    box-shadow: none;
    border-radius: 0;
  }
}

.load-more-trigger {
  text-align: center;
  height: 1px;
}

.sticky-day-header__left {
  display: flex;
  align-items: center;
  gap: var(--attendee-gap-md);

  .back-button {
    flex-shrink: 0;
  }

  h3 {
    margin: 0 !important;
  }
}

.mobile-filter-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  background: white;

  &__content {
    position: relative;
    width: 100%;
    max-height: 100%;
    border-radius: 10px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    height: 100%;
  }

  &__header,
  &__footer {
    flex-shrink: 0;
  }

  &__header {
    border-bottom: 1px solid var(--attendee-border-color);
    padding: var(--attendee-gap-md);
  }

  &__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-top: 1px solid var(--attendee-border-color);
    padding: var(--attendee-gap-lg) var(--attendee-gap-md) 28px var(--attendee-gap-md)
  }

  &__body {
    padding: 23px;
    overflow-y: auto;
    flex-grow: 1;
    padding-bottom: 50px;
  }
}

.agenda-filter-mobile-days {
  position: sticky;
  background-color: white;
  border-bottom: 1px solid var(--attendee-border-color);
  z-index: 22;
  display: none;

  @media (max-width: 900px) {
    display: block;
  }
}

.agenda-day {
  margin-bottom: 50px;

  @media (max-width: 900px) {
    margin-bottom: 20px;
  }
}

.agenda-filter-wrapper {
  width: 400px;

  @media (max-width: 1300px) {
    width: 300px;
  }

  @media (max-width: 900px) {
    order: 1;
  }

  @media (max-width: 1100px) {
    display: none;
  }
}

.agenda-page {
  --agenda-sticky-offset: calc(
    var(--attendee-header-height) + var(--attendee-gap-md) + var(--attendee-gap-lg)
  );

  .agenda-group {
    @media (max-width: 900px) {
      order: 2;
    }
  }

  h3 {
    color: var(--attendee-text-primary-color);
    font-size: 21px;
    margin-bottom: var(--attendee-gap-md);

    @media (max-width: 900px) {
      font-size: 16px;
      padding: var(--attendee-gap-md);
      padding-left: 0;
      margin-bottom: 0;
      text-align: left;
    }
  }
}

.agenda-filter {
  padding: var(--attendee-gap-lg);
  box-shadow: var(--attendee-box-shadow);
  background-color: var(--attendee-card-color);
  border-radius: var(--attendee-card-border-radius);
  position: sticky;
  top: 115px;
  height: calc(100vh - var(--attendee-header-height) - var(--attendee-gap-lg) - var(--attendee-gap-lg) - var(--attendee-gap-md));
  overflow: auto;

  &__top {
    border-bottom: 1px solid var(--attendee-border-color);
    padding-bottom: var(--attendee-gap-md);
    margin-bottom: var(--attendee-gap-md);
  }

  @media (max-width: 900px) {
    display: none;
  }
}

@media (min-width: 901px) {
  .mtb-desktop-15 {
    margin-top: 15px !important;
    margin-bottom: 15px !important;
  }
}
</style>
